import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPaginationSticky } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { useContext, useMemo } from "react";
import { MdSettings } from "react-icons/md";
import { useNavigate } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { TicketsQueryQs } from "@megaron/support-contracts";

import { SupportTicketsFilters } from "../list/SupportTicketsFilters";
import { SupportTicketsMobileList } from "../list/SupportTicketsMobileList";
import { SupportTicketsTable } from "../list/SupportTicketsTable";
import { useSupportTicketsFilters } from "../list/useSupportTicketsFilters";

import type { SupportTicketsFilterValues } from "../list/SupportTicketsFilters";
import type { SupportTicketsFiltersType } from "../list/useSupportTicketsFilters";
const pageSize = 20;

function toFilterValues(f: SupportTicketsFiltersType): SupportTicketsFilterValues {
  const sort =
    f.sort !== undefined && f.sortOrder !== undefined ? { field: f.sort, order: f.sortOrder } : undefined;

  return {
    searchText: f.searchText ?? "",
    status: f.status ?? [],
    priority: f.priority ?? [],
    category: f.category ?? [],
    tags: f.tags?.length ? f.tags : undefined,
    sort,
    myTickets: f.myTickets,
    isRead: f.isRead,
  };
}

function toTicketsListQuery(filters: SupportTicketsFiltersType): TicketsQueryQs {
  const page = filters.page ?? 0;
  const offset = page * pageSize;

  return {
    offset,
    limit: pageSize,
    searchText: filters.searchText || undefined,
    status: filters.status?.length ? filters.status : undefined,
    priority: filters.priority?.length ? filters.priority : undefined,
    category: filters.category?.length ? filters.category : undefined,
    tags: filters.tags?.length ? filters.tags : undefined,
    sort: filters.sort,
    sortOrder: filters.sortOrder === "ASC" ? "asc" : filters.sortOrder === "DESC" ? "desc" : undefined,
    myTickets: filters.myTickets,
    isRead: filters.isRead,
    contactEmail: undefined,
    customerUuid: undefined,
  };
}

function toUniqueNormalizedTags(tags: string[] | undefined) {
  return tags?.map((tag) => tag.trim().toLowerCase()).filter((tag, index, all) => tag.length > 0 && all.indexOf(tag) === index);
}

export const SupportTicketsPage = ({
  basePath = "/support/tickets",
  settingsPath = "/support/settings",
  createPath = "/support/tickets/new",
}: {
  basePath?: string;
  settingsPath?: string;
  createPath?: string;
}) => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const auth = useContext(IamAuthContext);
  const canAccessSupportSettings = useMemo(() => {
    const roles = auth?.iamUser?.roles ?? [];
    return roles.includes("support.admin") || roles.includes("crm.admin");
  }, [auth]);
  const canCreateSupportTicket = useMemo(() => {
    const roles = auth?.iamUser?.roles ?? [];
    return (
      roles.includes("support.user") ||
      roles.includes("crm.user") ||
      roles.includes("support.admin") ||
      roles.includes("crm.admin")
    );
  }, [auth]);
  const { filters, setFilter, setFilters } = useSupportTicketsFilters();
  const page = filters.page ?? 0;
  const ticketsListQuery = toTicketsListQuery(filters);

  const ticketsQuery = useClientManager("support")
    .ticketsList()
    .useQuery(ticketsListQuery);

  return (
    <Page
      css={{
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        gap: "1rem",
      }}
    >
      <PageHeader
        actions={
          canCreateSupportTicket || canAccessSupportSettings ? (
            <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {canCreateSupportTicket ? (
                <Button variant="primary" size="medium" onClick={() => navigate(createPath)}>
                  Dodaj
                </Button>
              ) : null}
              {canAccessSupportSettings ? (
                <Button icon={<MdSettings />} variant="text" size="small" onClick={() => navigate(settingsPath)}>
                  Ustawienia
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      >
        Zgłoszenia
      </PageHeader>

      <SearchWithPaginationSticky
        onSearchChange={setFilter("searchText")}
        searchValue={filters.searchText || ""}
        page={page}
        onPageChange={(p) => {
          setFilter("page")(p);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageSize={pageSize}
        itemCount={ticketsQuery.data?.count}
      />

      <SupportTicketsFilters
        values={toFilterValues(filters)}
        onChange={(patch) => {
          const normalizedTags = toUniqueNormalizedTags(patch.tags);
          const nextTags = Object.prototype.hasOwnProperty.call(patch, "tags")
            ? (normalizedTags && normalizedTags.length > 0 ? normalizedTags : undefined)
            : filters.tags;
          const nextSort = Object.prototype.hasOwnProperty.call(patch, "sort")
            ? patch.sort
            : filters.sort && filters.sortOrder
              ? { field: filters.sort, order: filters.sortOrder }
              : undefined;
          setFilters({
            ...filters,
            ...patch,
            sort: nextSort?.field,
            sortOrder: nextSort?.order,
            tags: nextTags,
            page: 0,
          });
        }}
      />

      <QuerySkeleton query={ticketsQuery}>
        {(data) =>
          isMobile ? (
            <SupportTicketsMobileList items={data.items} basePath={basePath} />
          ) : (
            <SupportTicketsTable items={data.items} basePath={basePath} isLoading={!ticketsQuery.data} />
          )
        }
      </QuerySkeleton>
    </Page>
  );
};
